import { ApiClientAxios, apiFactoryConfig } from "@repo/api";
import { useInfiniteQuery, type InfiniteData, type UseInfiniteQueryOptions } from "@tanstack/react-query";
import { formatISO } from "date-fns";
import { ms } from "humanize-ms";
import { useMemo } from "react";

export type UseFufiTransactionsMoneyInsParams = {
    contractId: string;
    moneyInsPageSize?: number;
    moneyInsFrom?: Date;
    moneyInsTo?: Date;
    moneyInsPayerTypes?: Array<ApiClientAxios.FufiContributionType> | null;
    moneyInsPaymentTypes?: Array<ApiClientAxios.FufiPaymentType> | null;
    options?: Partial<
        UseInfiniteQueryOptions<
            ApiClientAxios.FufiFinancialInfoResponse,
            unknown,
            InfiniteData<ApiClientAxios.FufiFinancialInfoResponse>,
            unknown[],
            number
        >
    >;
};

export const useFufiTransactionsMoneyIns = ({
    contractId,
    moneyInsPageSize = 5,
    moneyInsFrom,
    moneyInsTo,
    moneyInsPayerTypes,
    moneyInsPaymentTypes,
    options,
}: UseFufiTransactionsMoneyInsParams) => {
    const api = ApiClientAxios.ContractsLifeApiFactory(...apiFactoryConfig);
    const toIso = (v?: Date) => (v ? formatISO(v) : undefined);

    const query = useInfiniteQuery<
        ApiClientAxios.FufiFinancialInfoResponse,
        unknown,
        InfiniteData<ApiClientAxios.FufiFinancialInfoResponse>,
        unknown[],
        number
    >({
        queryKey: [
            "FufiTransactionsMoneyIns",
            contractId,
            moneyInsPageSize,
            toIso(moneyInsFrom),
            toIso(moneyInsTo),
            moneyInsPayerTypes,
            moneyInsPaymentTypes,
        ],
        initialPageParam: 1,
        queryFn: async ({ pageParam }) => {
            const request: ApiClientAxios.FufiFinancialInfoQuery = {
                moneyInsPageSize,
                moneyInsPage: pageParam,
                moneyInsFrom: toIso(moneyInsFrom),
                moneyInsTo: toIso(moneyInsTo),
                moneyInsPayerTypes,
                moneyInsPaymentTypes,
            };

            return (await api.getLifeFufiFinancialInfoPost(contractId, request)).data;
        },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            const loaded = allPages.reduce((acc, page) => acc + (page.moneyIns?.length ?? 0), 0);
            if (lastPage.moneyInsCount && loaded < lastPage.moneyInsCount) {
                return lastPageParam + 1;
            }
            return undefined;
        },
        gcTime: ms("1h"),
        staleTime: ms("1h"),
        ...options,
    });

    const flatData = useMemo(
        () => (query?.data ? query.data.pages.flatMap((page) => page.moneyIns || []) : []),
        [query.data],
    );

    return {
        query,
        flatData,
    };
};
